import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  isPlatformBrowser,
  NgClass,
  NgStyle,
  NgIf,
  NgTemplateOutlet
} from '@angular/common';
import { Router } from '@angular/router';
import { AppSettings } from '@Shared/app.settings';
import { Subscription } from 'rxjs';
import { ScreenSizeService } from '@Shared/services';
import { BasicPropertyParameters, VCard, VCardFormatter } from 'ngx-vcard';
import { DomSanitizer } from '@angular/platform-browser';
import {
  Benefits,
  CompensationTypes,
  PitchCardTypeEnum,
  WorkType
} from '@App/common/enums';
import { BusinessPitch } from '@App/common/models';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { BusinessService } from '@App/common/services';
import { Tooltip } from 'primeng/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { PhonePipe } from '@App/modules/pitch-card-shared/pipes';
import { EpRestrictComponent } from '@App/shared';

declare const Hls: any;
declare const Calendly: any;

@Component({
  selector: 'app-search-result-thumbnail',
  templateUrl: './search-result-thumbnail.component.html',
  styleUrls: ['./search-result-thumbnail.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgStyle,
    NgClass,
    Tooltip,
    TranslatePipe,
    PhonePipe,
    NgTemplateOutlet,
    EpRestrictComponent,
    SidebarComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SearchResultThumbnailComponent
  implements OnInit, OnChanges, OnDestroy, AfterContentChecked
{
  PitchCardType = PitchCardTypeEnum;
  isBrowser: boolean;
  icons = [];
  isShowRestrict = false;
  @Input() index;
  @Input() uniqueId: string;
  @Input() isMobilePreview = false;
  @Input() biggerText = false;

  @Input() businessDetails: any;
  @Input() contacted;
  @Input() demo = false;
  @Input() isContactMeButtonClicked = false;
  @Input() withShare = false;
  @Input() withVideoTestimonial = false;
  @Input() withContact = false;
  @Input() withSave = false;
  @Input() withMoreInfo = false;
  @Input() withAttachment = false;
  @Input() comingSoon = false;
  @Input() disabled = false;
  @Input() pocket = false;
  @HostBinding('style.--name-size')
  nameSize = 1;
  @HostBinding('style.--title-size')
  titleSize = 1;
  @HostBinding('style.--position-title-size')
  positionTitleSize = 1;
  @Input() scale = 1.0;
  @Input() autoScale = false;
  @Input() blur = false;
  @Output() companyImages: EventEmitter<{
    title: string;
    businessDetails: BusinessPitch;
  }> = new EventEmitter();
  @Output() isVideoLoaded: EventEmitter<ElementRef> = new EventEmitter();
  uploadedFiles = [];
  videoThumbSrc = 'assets/images/whatsunique.png';
  googleMapLink: any;
  formattedAddress: any;
  isCoverLandscape = true;
  playVideo = false;
  initVideo = false;
  mutedVideo = true;
  isChangedMuteMode: boolean;
  isVideoPlayed = false;
  isPlayingVideo = false;
  averageCustomerRating = 0;
  averageQualityRating = 0;
  hls: any;
  AppSettings = AppSettings;
  workTypes = WorkType;
  compensationTypes = CompensationTypes;
  benefits = Benefits;
  jobConditionsPopup: any;
  isJobConditionsPopupOpen = false;
  locationAddress = '';
  compensationDescription = '';
  nameOne: string;
  nameTwo: string;
  isContactMenuOpen = false;
  isMobileContactMenuOpen = false;
  currentContactMenu: any;
  isMobile: boolean;
  isDesktop: boolean;
  isJobCard: boolean;
  workTypeList = '';
  scheduled: any;
  $analyticsSub = new Subscription();
  countdownTimer: number = null;
  videoDuration: number = null;
  timer: any;
  vCard: VCard;
  vCardUrl: any; // SafeUrl;
  @ViewChild('coverImage') coverImage: ElementRef;
  @ViewChild('cardBusinessName') cardBusinessName: ElementRef;
  @ViewChild('contactMenuParent') contactMenuParent: ElementRef;
  private _objectUrl: string;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private businessService: BusinessService,
    private screenSizeService: ScreenSizeService,
    private elRef: ElementRef,
    private domSanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isMobile = this.screenSizeService.isMobile;
    this.isDesktop = this.screenSizeService.isDesktop;
  }

  get isAllCompleted() {
    return true;
  }

  @HostBinding('style.--scale-factor')
  get scaleFactor() {
    return this.scale;
  }

  ngOnInit() {
    if (this.businessDetails) {
      this.checkIsBusinessDetailsAvailable();
      this.setRatings();
      this.nameSize =
        this.businessDetails?.businessType === 'service' ||
        this.businessDetails?.businessType === 'employee'
          ? this.calcTitleSize()
          : this.calcNameSize();
      this.titleSize =
        this.businessDetails?.businessType === 'service' ||
        this.businessDetails?.businessType === 'employee'
          ? this.calcNameSize()
          : this.calcTitleSize();
      this.positionTitleSize =
        this.businessDetails?.businessType === 'job'
          ? this.calcPositionTitleSize()
          : 1;
      if (!this.businessDetails?.businessType) {
        this.businessDetails.businessType = PitchCardTypeEnum.Basic;
      }
      if (this.businessDetails?.businessName?.includes(' | ')) {
        const namesArray = this.businessDetails.businessName.split(' | ');
        this.nameOne = namesArray[0];
        this.nameTwo = namesArray[1];
      } else {
        this.nameOne = this.businessDetails.businessName;
        this.nameTwo = null;
      }

      this.isJobCard =
        this.businessDetails?.businessType === PitchCardTypeEnum.Job;
      if (this.isJobCard) {
        this.setWorkTypesList();
        this.getLocation();
        this.calculateCompensationDescription();
      }

      this.populateVCard();

      this.cdr.detectChanges();
    }
  }

  calcNameSize() {
    if (!this.businessDetails || !this.businessDetails.businessName) {
      return 1;
    }

    const koef = this.businessDetails.businessName.length - 20;

    if (koef < 0) {
      return 1;
    } else {
      return 1 - koef / 100;
    }
  }

  calcTitleSize() {
    if (!this.businessDetails || !this.businessDetails.title) {
      return 1;
    }

    const koef = this.businessDetails.title.length - 25;

    if (koef < 0) {
      return 1;
    } else {
      return 1 - koef / 100;
    }
  }

  calcPositionTitleSize() {
    if (this.businessDetails?.positions?.length) {
      let position: any = '';
      if (this.businessDetails?.positions[0].hasOwnProperty('name')) {
        position = this.businessDetails?.positions[0]?.name
          ? this.businessDetails.positions[0].name
          : '';
      } else {
        position = this.businessDetails?.positions[0]
          ? this.businessDetails.positions[0]
          : '';
      }

      if (position.length - 15 < 0) {
        return 1;
      } else {
        return 1 - (position.length + 9) / 100;
      }
    } else {
      return 1;
    }
  }

  ngAfterContentChecked() {
    if (this.autoScale) {
      setTimeout(() => this.calcScale(), 100);
    }
    if (this.demo) {
      this.nameSize =
        this.businessDetails?.businessType === 'service' ||
        this.businessDetails?.businessType === 'employee'
          ? this.calcTitleSize()
          : this.calcNameSize();
      this.titleSize =
        this.businessDetails?.businessType === 'service' ||
        this.businessDetails?.businessType === 'employee'
          ? this.calcNameSize()
          : this.calcTitleSize();
      this.positionTitleSize =
        this.businessDetails?.businessType === 'job'
          ? this.calcPositionTitleSize()
          : 1;
    }
  }

  calcScale() {
    const pitchCardWrapper = this.elRef.nativeElement.parentElement;
    if (pitchCardWrapper.clientWidth * 0.8 < 370) {
      this.scale = (pitchCardWrapper.clientWidth * 0.8) / 370;
    } else if (pitchCardWrapper.clientHeight * 0.8 < 600) {
      const scaleFactorHeight = (pitchCardWrapper.clientHeight * 0.8) / 600;
      if (scaleFactorHeight < this.scaleFactor) {
        this.scale = scaleFactorHeight;
      }
    } else {
      this.scale = 1;
    }
  }

  calcCover() {
    this.isCoverLandscape =
      this.coverImage.nativeElement.naturalWidth /
        this.coverImage.nativeElement.naturalHeight >
      1.33;
    // if (this.videoPlayer?.vPlayer) {
    //   this.isVideoLoaded.emit(this.videoPlayer.vPlayer);
    // } else {
    //   this.isVideoLoaded.emit();
    // }
  }

  ngOnDestroy() {
    try {
      this.detachMedia();
    } catch (error) {
      console.log(error);
    }
    if (this.$analyticsSub) {
      this.$analyticsSub.unsubscribe();
    }
    if (this.jobConditionsPopup && this.isJobConditionsPopupOpen) {
      this.jobConditionsPopup.hide();
    }
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('message', this.scheduled, false);
      if (this._objectUrl) {
        window.URL.revokeObjectURL(this._objectUrl);
      }
    }
  }

  ngOnChanges() {
    this.videoThumbSrc = 'assets/images/comingsoon.png';

    if (this.businessDetails) {
      if (!this.demo) {
        this.setRatings();
      }

      if (this.isMobile && !this.businessDetails?.id) {
        this.scale = 0.7;
      }

      this.nameSize =
        this.businessDetails?.businessType === 'service' ||
        this.businessDetails?.businessType === 'employee'
          ? this.calcTitleSize()
          : this.calcNameSize();
      this.checkIsBusinessDetailsAvailable();
      this.titleSize =
        this.businessDetails?.businessType === 'service' ||
        this.businessDetails?.businessType === 'employee'
          ? this.calcNameSize()
          : this.calcTitleSize();
      this.positionTitleSize =
        this.businessDetails?.businessType === 'job'
          ? this.calcPositionTitleSize()
          : 1;
      if (this.businessDetails?.businessName?.includes(' | ')) {
        const namesArray = this.businessDetails.businessName.split(' | ');
        this.nameOne = namesArray[0];
        this.nameTwo = namesArray[1];
      } else if (this.businessDetails?.businessType === 'service') {
        this.nameOne = this.businessDetails.businessName;
        this.nameTwo = null;
      } else {
        this.nameOne = null;
        this.nameTwo = null;
      }

      this.isJobCard =
        this.businessDetails?.businessType === PitchCardTypeEnum.Job;
      if (this.isJobCard) {
        this.setWorkTypesList();
        this.getLocation();
        this.calculateCompensationDescription();
      }

      this.populateVCard();

      this.cdr.detectChanges();
    }
  }

  detectChanges() {
    this.cdr.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.isContactMenuOpen && this.contactMenuParent?.nativeElement) {
      const contactMenu = this.contactMenuParent.nativeElement;
      if (!contactMenu.contains(event.target as Node)) {
        this.isContactMenuOpen = false;
        this.cdr.detectChanges();
      }
    }
  }

  setRatings() {
    if (
      this.businessDetails &&
      this.businessDetails.averageCustomerRating &&
      this.businessDetails.averageQualityRating
    ) {
      this.averageCustomerRating =
        +this.businessDetails.averageCustomerRating.toFixed(1);
      this.averageQualityRating =
        +this.businessDetails.averageQualityRating.toFixed(1);
    }
  }

  checkIsBusinessDetailsAvailable() {
    if (
      this.businessDetails.employeePictureThumnailUrl &&
      this.businessDetails.employeePictureFileIds
    ) {
      this.uploadedFiles = [];
      const employeeImgFileIds =
        this.businessDetails.employeePictureFileIds.split(',');
      employeeImgFileIds.forEach((elm, idx) => {
        this.uploadedFiles[idx] = {
          fileId: elm,
          thumbnailFileUrl: this.businessDetails.employeePictureThumnailUrl[idx]
        };
      });
    }

    if (this.businessDetails.address) {
      let query = this.businessDetails?.address.trim();

      if (
        !query.includes(this.businessDetails?.cityName) &&
        !(
          query.includes(this.businessDetails?.stateName) ||
          query.includes(this.businessDetails?.stateCode)
        )
      ) {
        if (this.businessDetails?.cityName) {
          query += `, ${this.businessDetails?.cityName}`;
        }

        if (this.businessDetails?.stateName) {
          query += `, ${this.businessDetails?.stateName}`;
        }
      }

      if (!query.includes(this.businessDetails?.zip)) {
        if (this.businessDetails?.zip) {
          query += `, ${this.businessDetails?.zip}`;
        }
      }

      this.formattedAddress = query;

      this.googleMapLink = `https://www.google.com/maps/search/?api=1&query=${query}`;
    }
  }

  onTitleClick(title) {
    if (this.isMobile && title === 'saveAsFavorite') {
      const vcards = document.getElementsByClassName('contracts-vcf');
      if (vcards.length) {
        for (let index = 0; index < vcards.length; index++) {
          vcards[index].setAttribute('id', '');
        }
        for (let index = 0; index < vcards.length; index++) {
          const element = vcards[index];
          if (
            element['href'] ===
            this.vCardUrl.changingThisBreaksApplicationSecurity
          ) {
            vcards[index].setAttribute('id', 'downloadvcf');
            break;
          }
        }
      }
    }

    if (
      (this.disabled && !this.withShare) ||
      (title === 'applyJobNowDialog' && this.router.url.includes('create')) ||
      (title === 'showJobRequirementsDialog' &&
        this.router.url.includes('create')) ||
      (title !== 'playVideo' &&
        title !== 'done' &&
        title !== 'contactMe' &&
        this.blur)
    ) {
      return;
    }

    if (this.businessDetails) {
      this.stopVideo();
      this.companyImages.emit({
        title,
        businessDetails: this.businessDetails
      });
    }
    if (title === 'addToContact') {
      this.businessService
        .addToContactAnalytics(this.businessDetails.id)
        .subscribe((res) => {});
    }
  }

  toggleContactMenu(event, parent = null) {
    if (this.blur) {
      return;
    }

    this.isContactMenuOpen = !this.isContactMenuOpen;

    if (this.isContactMenuOpen) {
      setTimeout(() => {
        this.positionContactMenu(event);
      }, 0);
    }
  }

  openBottomContactMenu() {
    if (this.blur) {
      return;
    }
    this.isMobileContactMenuOpen = true;
  }

  toggleAsFavorite() {
    if (this.businessDetails && this.businessDetails.id && !this.blur) {
      if (this.businessDetails.isFavoriteBusiness) {
        this.onTitleClick('removeAsFavorite');
      } else {
        this.onTitleClick('saveAsFavorite');
      }
    }
  }

  goToWatchedVideoReviewList(id) {
    if (id) {
      this.router.navigate([`company-review/${id}`]);
    }
  }

  stopVideo() {
    if (this.businessDetails && this.businessDetails.isVideoPlaying) {
      this.businessDetails.isVideoPlaying = false;
      this.initVideo = false;
      this.cdr.detectChanges();
    }
  }

  runVideo(e) {
    e.preventDefault();
    if (
      ((!this.businessDetails?.isVideoPlaying && this.isVideoPlayed) ||
        (this.pocket && !this.businessDetails?.isVideoPlaying)) &&
      this.businessDetails.deliveryType === 1
    ) {
      this.initVideo = true;
      // this.loadVideo();
    }
  }

  runVideoMobile(e) {
    // e.preventDefault();
    // if (
    //   (!this.businessDetails?.isVideoPlaying && this.isVideoPlayed) ||
    //   (this.pocket && !this.businessDetails?.isVideoPlaying) ||
    //   (this.isMobilePreview && !this.businessDetails?.isVideoPlaying)
    // ) {
    //   this.initVideo = true;
    //   if (
    //     this.businessDetails?.videoFileUrl &&
    //     this.businessDetails.deliveryType === 1
    //   ) {
    //     this.loadVideo();
    //     if (!this.isChangedMuteMode && this.mutedVideo) {
    //       this.videoPlayer?.initPlayer();
    //       this.videoPlayer.player.muted(false);
    //       this.isChangedMuteMode = true;
    //       this.isPlayingVideo = true;
    //     }
    //     // else if (this.isplayingVideo) {
    //     //   this.videoPlayer.player.pause();
    //     //   this.isplayingVideo = false;
    //     // } else{
    //     //   this.videoPlayer.player.play();
    //     //   this.isplayingVideo = true;
    //     // }
    //   }
    // }
  }

  onTimeUpdate(event) {
    if (event.target.duration) {
      if (!this.videoDuration) {
        this.playVideo = true;
        this.setCountDownTimer(event.target.duration);
      }
      if (
        event.currentTarget &&
        event.currentTarget.currentTime >= event.currentTarget.duration
      ) {
        this.businessDetails.isVideoPlaying = false;
      }
      if (!this.businessDetails.isVideoPlaying) {
        this.playVideo = false;
        this.initVideo = false;
        this.videoDuration = null;
        this.countdownTimer = null;
        clearInterval(this.timer);
        this.detachMedia();
        this.cdr.detectChanges();
      }
    }
  }

  handleUploadVideoError() {
    this.initVideo = false;
  }

  setCountDownTimer(duration) {
    this.videoDuration = Math.floor(duration);
    const VISIBLE_TIME = 4;
    const FADE_OUT_TIME = 1;

    if (
      this.videoDuration > VISIBLE_TIME &&
      this.businessDetails.isVideoPlaying
    ) {
      this.countdownTimer = !this.countdownTimer
        ? this.videoDuration
        : this.countdownTimer;
      this.timer = setInterval(() => {
        this.countdownTimer = --this.countdownTimer;
        if (this.countdownTimer === this.videoDuration - VISIBLE_TIME) {
          const el = document.getElementById('countdown-timer');
          el?.classList.add('fade-out');
        }
        if (
          this.countdownTimer <=
          this.videoDuration - VISIBLE_TIME - FADE_OUT_TIME
        ) {
          clearInterval(this.timer);
          this.countdownTimer = null;
        }
      }, 1000);
    }
  }

  showCalendar(calendarLink) {
    if (isPlatformBrowser(this.platformId)) {
      window.open(calendarLink, '_blank');
    }
  }

  showCalendly(calendarLink) {
    return new Promise((resolve) => {
      if (isPlatformBrowser(this.platformId)) {
        Calendly.showPopupWidget(calendarLink, null, {
          parentElement: document.getElementsByClassName(
            'banner-search-wrap container'
          )[0]
        });
      }
      resolve(null);
    });
  }

  handleSubmitEventFromCalendly() {
    if (isPlatformBrowser(this.platformId)) {
      this.scheduled = window.addEventListener('message', (e) => {
        if (e.data.event === 'calendly.event_scheduled') {
          Calendly.closePopupWidget();
          this.addScheduledBusinessAnalytics();
        }
      });
    }
  }

  addScheduledBusinessAnalytics() {
    this.$analyticsSub.add(
      this.businessService
        .scheduledBusiness([this.businessDetails.id])
        .subscribe(() => {})
    );
  }

  addCallsAnalytics() {
    if (isPlatformBrowser(this.platformId)) {
      window.location.href = `tel:${this.businessDetails.contactNumber}`;
    }
    this.$analyticsSub.add(
      this.businessService
        .contactCall([this.businessDetails.id])
        .subscribe(() => {})
    );
  }

  addEmailAnalytics() {
    if (isPlatformBrowser(this.platformId)) {
      window.location.href = `mailto:${this.businessDetails.email}`;
    }
    this.$analyticsSub.add(
      this.businessService
        .contactEmail([this.businessDetails.id])
        .subscribe(() => {})
    );
  }

  addLocationAnalytics() {
    this.$analyticsSub.add(
      this.businessService
        .contactDirections([this.businessDetails.id])
        .subscribe(() => {})
    );
  }

  detachMedia() {
    this.playVideo = false;
    if (this.isBrowser && this.hls && Hls.isSupported()) {
      this.hls.stopLoad();
      this.hls.detachMedia();
      this.hls.destroy();
    }
  }

  setWorkTypesList() {
    if (this.businessDetails?.workTypes?.length) {
      this.workTypeList = this.businessDetails?.workTypes
        .map((x) => x.name)
        .join(', ');
    }

    if (this.workTypeList.length > 22) {
      this.workTypeList =
        this.businessDetails.workTypes[0].name +
        ' +' +
        (this.businessDetails.workTypes.length - 1);
    }
  }

  getLocation() {
    this.locationAddress = this.businessService.getLocation(
      this.businessDetails
    );
  }

  calculateCompensationDescription() {
    this.compensationDescription = this.businessDetails
      ? this.businessService.calculateCompensationDescription(
          this.businessDetails
        )
      : '';
    this.compensationDescription =
      this.compensationDescription.trim() !== '' &&
      this.compensationDescription.trim() !== '-'
        ? this.compensationDescription
        : '';
  }

  populateVCard() {
    if (
      this.businessDetails?.title ||
      this.businessDetails?.businessName ||
      this.businessDetails?.alias
    ) {
      // Format phone number for iOS
      const formattedPhone =
        this.screenSizeService.os === 'iOS'
          ? this.formatPhoneNumber(this.businessDetails.contactNumber)
          : this.businessDetails.contactNumber;

      this.vCard = {
        version: this.screenSizeService.os === 'iOS' ? '4.0' : '3.0',
        name: this.populateVCardName(),
        telephone: [],
        email:
          this.screenSizeService.os === 'iOS'
            ? [this.businessDetails.email]
            : [],
        url: encodeURI(
          `${isPlatformBrowser(this.platformId) ? window.location.origin : ''}/card/${this.businessDetails.alias}`
        ),
        organization: this.populateOrgName(),
        photo: this.businessDetails.businessLogoThumbnailUrl
          ? 'data:image/jpeg;base64,' +
            (isPlatformBrowser(this.platformId)
              ? window.btoa(this.businessDetails.businessLogoThumbnailUrl)
              : '')
          : ''
      };

      if (this.screenSizeService.os !== 'iOS') {
        const emailParam: BasicPropertyParameters = { type: 'work' };
        const email = {
          value: this.businessDetails.email,
          param: emailParam
        };
        this.vCard.email.push(email);
      }

      const phoneParam: BasicPropertyParameters = { type: 'cell' };
      const phone = {
        value: formattedPhone,
        param: phoneParam
      };
      this.vCard.telephone.push(phone);

      const vCardString = VCardFormatter.getVCardAsString(this.vCard);

      // Fix photo encoding for better compatibility
      const fixedVCardString = vCardString.replace(
        /PHOTO:data:image\/jpeg;base64,/g,
        'PHOTO;ENCODING=b;TYPE=JPEG:'
      );

      this.populateLink(
        this.screenSizeService.os === 'iOS'
          ? this.populateCustomVCardUrl(fixedVCardString)
          : fixedVCardString
      );
    }
  }

  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) {
      return '';
    }

    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Format for iOS: +1XXXXXXXXXX
    if (cleaned.length === 10) {
      return '+1' + cleaned;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return '+' + cleaned;
    }

    return phoneNumber;
  }

  populateVCardName(): { firstNames: string; lastNames: string } {
    const fullNameObject: { firstNames: string; lastNames: string } = {
      firstNames: '',
      lastNames: ''
    };
    const fullName = this.populateFullName();
    const splitName = fullName.split(' ');
    if (splitName?.length > 1) {
      splitName.map((item, index) => {
        if (index === 0) {
          fullNameObject.firstNames = item;
        } else {
          fullNameObject.lastNames = fullNameObject.lastNames + item + ' ';
        }
      });
      return fullNameObject;
    } else {
      return { firstNames: this.populateFullName(), lastNames: '' };
    }
  }

  populateFullName() {
    if (
      this.businessDetails?.businessType === PitchCardTypeEnum.Service ||
      this.businessDetails?.businessType === PitchCardTypeEnum.Basic ||
      this.businessDetails?.businessType === PitchCardTypeEnum.Employee
    ) {
      return this.businessDetails?.title
        ? this.checkStringName(this.businessDetails.title)
        : this.checkStringName(
            this.businessDetails?.businessName
              ? this.businessDetails.businessName
              : ''
          );
    } else if (
      this.businessDetails?.businessType === PitchCardTypeEnum.Resume
    ) {
      return this.businessDetails?.businessName
        ? this.checkStringName(this.businessDetails.businessName)
        : this.checkStringName(
            this.businessDetails?.title ? this.businessDetails.title : ''
          );
    } else {
      return '';
    }
  }

  checkStringName(str) {
    return str.includes('|') ? str.replace(' | ', ' ') : str;
  }

  populateOrgName(): string {
    if (
      this.businessDetails?.businessType === PitchCardTypeEnum.Service ||
      this.businessDetails?.businessType === PitchCardTypeEnum.Basic ||
      this.businessDetails?.businessType === PitchCardTypeEnum.Employee
    ) {
      return this.businessDetails?.businessName
        ? this.checkStringName(this.businessDetails.businessName)
        : '';
    } else if (
      this.businessDetails?.businessType === PitchCardTypeEnum.Resume
    ) {
      return this.businessDetails?.title
        ? this.checkStringName(this.businessDetails.title)
        : '';
    } else {
      return '';
    }
  }

  populateCustomVCardUrl(str: string) {
    return str.replace('URL', 'URL;PitchCard');
  }

  populateLink(vCardString) {
    if (isPlatformBrowser(this.platformId)) {
      // Create a Blob with the vCard data
      const blob = new Blob([vCardString], { type: 'text/vcard' });

      // Create object URL and sanitize it
      const objectUrl = window.URL.createObjectURL(blob);
      this.vCardUrl = this.domSanitizer.bypassSecurityTrustUrl(objectUrl);

      // Store the object URL for cleanup
      this._objectUrl = objectUrl;
    }
  }

  private positionContactMenu(event: PointerEvent | MouseEvent) {
    if (!this.contactMenuParent?.nativeElement) {
      return;
    }

    const contactMenu = this.contactMenuParent.nativeElement;
    const triggerElement = event.target as HTMLElement;
    const triggerRect = triggerElement.getBoundingClientRect();
    const viewportWidth = isPlatformBrowser(this.platformId)
      ? window.innerWidth
      : 1024;
    const viewportHeight = isPlatformBrowser(this.platformId)
      ? window.innerHeight
      : 768;

    const menuWidth = 370 * this.scale;
    const menuHeight = 300 * this.scale;

    // Center horizontally relative to the screen
    let left = viewportWidth / 2 - menuWidth / 2;
    let top = triggerRect.bottom + 15; // 15px gap below trigger

    // Check if menu would overflow right edge
    if (left + menuWidth > viewportWidth) {
      left = viewportWidth - menuWidth - 10;
    }

    // Check if menu would overflow left edge
    if (left < 10) {
      left = 10;
    }

    // Check if menu would overflow bottom edge
    if (top + menuHeight > viewportHeight) {
      top = triggerRect.top - menuHeight - 15; // Position above trigger with 15px gap
    }

    // Ensure menu doesn't go off the top edge
    if (top < 10) {
      top = 10;
    }

    contactMenu.style.position = 'fixed';
    contactMenu.style.left = `${left}px`;
    contactMenu.style.top = `${top}px`;
    contactMenu.style.zIndex = '9999';
  }
}
